/**
 * Testimonial Carousel Block JavaScript
 * Sets CSS animation duration - content duplicated in Liquid for infinite scroll
 * Following badge carousel approach (CSS-only animation)
 * 
 * @param {HTMLElement} container - The main section container element.
 */
class TestimonialCarousel {
  /**
   * @param {HTMLElement} container
   */
  constructor(container) {
    this.container = container;
    this.track = this.container.querySelector('.testimonial-carousel__track');
    this.resizeObserver = null;
    
    this.settings = this.getSettings();
    this.init();
  }

  getSettings() {
    return {
      scrollDuration: parseFloat(this.container.dataset.scrollDuration || '30')
    };
  }

  init() {
    this.setupCarousel();
    this.bindEvents();
  }

  setupCarousel() {
    if (!this.track) return;

    // Get all testimonial cards
    const cards = this.track.querySelectorAll('.testimonial-card');
    const totalCards = cards.length / 3; // Since we triplicate in Liquid
    
    if (totalCards === 0) return;
    
    // Calculate the width of one complete set
    let totalWidth = 0;
    
    // Get the computed style to read the actual margin
    const containerStyles = getComputedStyle(this.container);
    const cardSpacing = parseFloat(
      containerStyles.getPropertyValue('--testimonial-card-spacing') || '18'
    );
    
    // Measure each card in the first set
    for (let i = 0; i < totalCards; i++) {
      const card = cards[i];
      if (card instanceof HTMLElement) {
        totalWidth += card.offsetWidth;
        // Add margin for all cards except the last one
        if (i < totalCards - 1) {
          totalWidth += cardSpacing;
        }
      }
    }
    
    // Set the precise scroll distance
    this.container.style.setProperty('--scroll-distance', `${totalWidth}px`);
    
    // Set CSS animation duration (no cloning needed - done in Liquid)
    this.container.style.setProperty('--testimonial-scroll-duration', `${this.settings.scrollDuration}s`);
  }

  bindEvents() {
    if (!this.track) return;
    
    // Set up resize observer to recalculate on size changes
    this.resizeObserver = new ResizeObserver(() => {
      this.setupCarousel();
    });
    this.resizeObserver.observe(this.container);
    
    // Handle theme editor events
    document.addEventListener('shopify:section:load', (event) => {
      if (event.target instanceof Element && event.target.contains(this.container)) {
        this.refresh();
      }
    });

    document.addEventListener('shopify:section:unload', (event) => {
      if (event.target instanceof Element && event.target.contains(this.container)) {
        this.destroy();
      }
    });
  }

  refresh() {
    this.settings = this.getSettings();
    this.setupCarousel();
  }

  destroy() {
    // Clean up resize observer
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }
}

// Initialize testimonial carousels when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  const carousels = document.querySelectorAll('[data-section-type="testimonial-carousel"]');
  
  carousels.forEach((carousel) => {
    if (carousel instanceof HTMLElement) {
      new TestimonialCarousel(carousel);
    }
  });
});

// Handle dynamic loading (for theme editor)
document.addEventListener('shopify:section:load', function(event) {
  if (event.target instanceof Element) {
    const carousel = event.target.querySelector('[data-section-type="testimonial-carousel"]');
    if (carousel instanceof HTMLElement) {
      new TestimonialCarousel(carousel);
    }
  }
}); 